import { useState, useEffect } from 'react'

import { getSessionHeaders } from '../../lib/serverSession';
interface AuditDiscrepancy {
  userId: number
  username: string
  name: string
  email: string
  earnedShares: number
  convertedShares: number
  adminConversions: number
  autoConversions: number
  sharesSent: number
  sharesReceived: number
  expectedBalance: number
  currentBalance: number
  difference: number
  status: 'MISSING' | 'EXTRA'
}

export default function ShareBalanceAuditDashboard() {
  const [discrepancies, setDiscrepancies] = useState<AuditDiscrepancy[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'missing' | 'extra'>('all')
  const [fixing, setFixing] = useState<number | null>(null)

  useEffect(() => {
    loadAuditData()
  }, [])

  const loadAuditData = async () => {
    setLoading(true)
    try {
      // Load REAL-TIME data directly from database
      console.log('[ShareBalanceAudit] Loading REAL-TIME data from database...')
      const response = await fetch('/api/admin/get-share-discrepancies')
      if (response.ok) {
        const data = await response.json()
        console.log('[ShareBalanceAudit] Loaded discrepancies:', data.discrepancies.length)
        console.log('[ShareBalanceAudit] Summary:', data.summary)
        setDiscrepancies(data.discrepancies || [])
      }
    } catch (error) {
      console.error('[ShareBalanceAudit] Error loading audit data:', error)
    } finally {
      setLoading(false)
    }
  }

  const runAudit = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/admin/run-audit', {
        method: 'POST'
      })
      
      if (response.ok) {
        await loadAuditData()
      }
    } catch (error) {
      console.error('Error running audit:', error)
    } finally {
      setLoading(false)
    }
  }

  const fixDiscrepancy = async (userId: number, difference: number) => {
    if (!confirm(`Are you sure you want to ${difference > 0 ? 'add' : 'remove'} ${Math.abs(difference).toFixed(2)} shares ${difference > 0 ? 'to' : 'from'} User ID ${userId}?`)) {
      return
    }

    setFixing(userId)
    try {
      console.log(`[ShareBalanceAudit] Fixing User ${userId}, difference: ${difference}`)

      const response = await fetch('/api/admin/fix-share-balance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId, difference })
      })

      console.log(`[ShareBalanceAudit] Response status: ${response.status}`)

      if (response.ok) {
        const result = await response.json()
        console.log(`[ShareBalanceAudit] Success:`, result)

        alert(`✅ Balance fixed successfully!\n\nOld Balance: ${result.oldBalance}\nAdjustment: ${result.adjustment > 0 ? '+' : ''}${result.adjustment}\nNew Balance: ${result.newBalance}`)

        // Reload REAL-TIME data from database
        console.log('[ShareBalanceAudit] Reloading real-time data...')
        await loadAuditData()
      } else {
        const error = await response.json()
        console.error(`[ShareBalanceAudit] Error response:`, error)
        alert(`❌ Error: ${error.error || error.message || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('[ShareBalanceAudit] Exception:', error)
      alert(`❌ Error fixing discrepancy: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setFixing(null)
    }
  }

  const filteredDiscrepancies = discrepancies.filter(d => {
    if (filter === 'missing') return d.status === 'MISSING'
    if (filter === 'extra') return d.status === 'EXTRA'
    return true
  })

  const totalMissing = discrepancies.filter(d => d.status === 'MISSING').reduce((sum, d) => sum + d.difference, 0)
  const totalExtra = discrepancies.filter(d => d.status === 'EXTRA').reduce((sum, d) => sum + Math.abs(d.difference), 0)

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Share Balance Audit Dashboard</h1>
        <button
          onClick={runAudit}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <span className={loading ? 'inline-block animate-spin' : ''}>🔄</span>
          Run Audit
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600 dark:text-white">Total Discrepancies</div>
          <div className="text-2xl font-bold">{discrepancies.length}</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg shadow">
          <div className="text-sm text-red-600 dark:text-red-400">Missing Shares</div>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {totalMissing.toFixed(2)}
          </div>
          <div className="text-sm text-red-500">${(totalMissing * 5).toFixed(2)} value</div>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg shadow">
          <div className="text-sm text-yellow-600 dark:text-yellow-400">Extra Shares</div>
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {totalExtra.toFixed(2)}
          </div>
          <div className="text-sm text-yellow-500">${(totalExtra * 5).toFixed(2)} value</div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg shadow">
          <div className="text-sm text-blue-600 dark:text-blue-400">Net Discrepancy</div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {(totalMissing - totalExtra).toFixed(2)}
          </div>
          <div className="text-sm text-blue-500">${((totalMissing - totalExtra) * 5).toFixed(2)} value</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}
        >
          All ({discrepancies.length})
        </button>
        <button
          onClick={() => setFilter('missing')}
          className={`px-4 py-2 rounded-lg ${filter === 'missing' ? 'bg-red-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}
        >
          Missing ({discrepancies.filter(d => d.status === 'MISSING').length})
        </button>
        <button
          onClick={() => setFilter('extra')}
          className={`px-4 py-2 rounded-lg ${filter === 'extra' ? 'bg-yellow-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}
        >
          Extra ({discrepancies.filter(d => d.status === 'EXTRA').length})
        </button>
      </div>

      {/* Discrepancies Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-4xl mb-4 animate-spin inline-block">🔄</div>
          <p>Loading audit data...</p>
        </div>
      ) : filteredDiscrepancies.length === 0 ? (
        <div className="text-center py-12 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="text-4xl text-green-600 mb-4">✅</div>
          <p className="text-green-600 dark:text-green-400 font-semibold">All balances are correct!</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white dark:bg-gray-800 rounded-lg shadow">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Breakdown
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Expected
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Current
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Discrepancy
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredDiscrepancies.map((disc) => (
                <tr key={disc.userId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-4">
                    <div className="font-medium">ID {disc.userId}: {disc.username}</div>
                    <div className="text-sm text-gray-500">{disc.name}</div>
                    <div className="text-sm text-white">{disc.email}</div>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <div>Earned: {disc.earnedShares?.toFixed(2) || '0.00'}</div>
                    <div>Converted: {disc.convertedShares?.toFixed(2) || '0.00'} ({disc.adminConversions || 0}A, {disc.autoConversions || 0}Auto)</div>
                    <div>Sent: -{disc.sharesSent?.toFixed(2) || '0.00'}</div>
                    <div>Received: +{disc.sharesReceived?.toFixed(2) || '0.00'}</div>
                  </td>
                  <td className="px-4 py-4 font-medium">
                    {disc.expectedBalance?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-4 font-medium">
                    {disc.currentBalance?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-4">
                    <div className={`flex items-center gap-2 ${disc.status === 'MISSING' ? 'text-red-600' : 'text-yellow-600'}`}>
                      <span>{disc.status === 'MISSING' ? '⚠️' : '❌'}</span>
                      <span className="font-bold">
                        {disc.status === 'MISSING' ? '+' : ''}{disc.difference?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      ${(Math.abs(disc.difference || 0) * 5).toFixed(2)} value
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => fixDiscrepancy(disc.userId, disc.difference)}
                      disabled={fixing === disc.userId}
                      className={`px-3 py-1 rounded text-sm font-medium ${
                        disc.status === 'MISSING'
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                      } disabled:opacity-50`}
                    >
                      {fixing === disc.userId ? 'Fixing...' : 'Fix Balance'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

